import { Link, useSearchParams } from 'react-router-dom';
import { sushiRestaurants } from '../../mocks/restaurants';
import { useState, useEffect } from 'react';
import { useSEO } from '../../utils/seo';

export default function SushiRestaurants() {
  const [searchParams] = useSearchParams();
  const [startIndex, setStartIndex] = useState(0);
  const [glowingRestaurant, setGlowingRestaurant] = useState<number | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<number | null>(null);
  const itemsPerPage = 6;
  const displayedRestaurants = sushiRestaurants.slice(startIndex, startIndex + itemsPerPage);
  const hasMore = startIndex + itemsPerPage < sushiRestaurants.length;

  useSEO({
    title: 'Best Sushi Restaurants Antalya Fresh Japanese Cuisine Guide',
    description: 'Discover the best sushi restaurants in Antalya serving fresh Japanese cuisine, authentic rolls, and sashimi. Find top-rated sushi restaurants in Antalya with reviews, ratings, locations, and contact information for an unforgettable dining experience.',
    keywords: 'sushi Antalya, Japanese restaurants Antalya, best sushi Antalya',
    canonical: '/sushi-restaurants',
    ogTitle: 'Best Sushi Restaurants in Antalya',
    ogDescription: 'Find the freshest sushi and authentic Japanese cuisine in Antalya',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Sushi Restaurants in Antalya',
      'url': '{{SITE_URL}}/sushi-restaurants',
      'description': 'Find the best sushi restaurants in Antalya',
      'mainEntity': {
        '@type': 'ItemList',
        'itemListElement': sushiRestaurants.map((restaurant, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'item': {
            '@type': 'Restaurant',
            'name': restaurant.name,
            'description': restaurant.description,
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': restaurant.address,
              'addressLocality': 'Antalya',
              'addressCountry': 'TR'
            },
            'telephone': restaurant.phone,
            'priceRange': restaurant.priceRange,
            'aggregateRating': {
              '@type': 'AggregateRating',
              'ratingValue': restaurant.rating,
              'bestRating': '5'
            },
            'servesCuisine': 'Japanese'
          }
        }))
      }
    }
  });

  useEffect(() => {
    const restaurantId = searchParams.get('restaurant');
    if (restaurantId) {
      const id = parseInt(restaurantId);
      setGlowingRestaurant(id);
      
      // Scroll to the restaurant
      setTimeout(() => {
        const element = document.getElementById(`restaurant-${id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Remove glow after 2 seconds
      setTimeout(() => {
        setGlowingRestaurant(null);
      }, 2000);
    }
  }, [searchParams]);

  const handleLoadMore = () => {
    if (hasMore) {
      setStartIndex(prev => prev + itemsPerPage);
    }
  };

  const toggleRestaurant = (id: number) => {
    setSelectedRestaurant(prev => prev === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/46496767-ecf9-45a1-9f97-898f0428276b.png" 
              alt="Antalya Eats Logo" 
              className="w-8 h-8 object-contain image-rendering-pixelated"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="text-lg font-bold tracking-tight">ANTALYA EATS</span>
          </Link>
          <nav className="flex gap-4">
            <Link to="/" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">Home</Link>
            <Link to="/about" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">About</Link>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <section className="pt-20 pb-8 px-4 bg-pink-400 border-b-4 border-black">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-32 h-32 mx-auto mb-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/ae2f4b8c-f4e6-4c33-b7e6-e554c1c48200.png"
              alt="Fresh Sushi"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">SUSHI RESTAURANTS</h1>
          <p className="text-base text-white">Find the <strong>freshest sushi</strong> in Antalya</p>
        </div>
      </section>

      {/* Restaurants List */}
      <main>
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid gap-4">
            {sushiRestaurants.map((restaurant) => (
              <article 
                key={restaurant.id}
                id={`restaurant-${restaurant.id}`}
                onClick={() => toggleRestaurant(restaurant.id)}
                className={`bg-white border-4 border-black p-4 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer relative ${
                  glowingRestaurant === restaurant.id ? 'animate-glow' : ''
                }`}
                style={glowingRestaurant === restaurant.id ? {
                  boxShadow: '0 0 20px 5px rgba(244, 114, 182, 0.8), 8px 8px 0px 0px rgba(0,0,0,1)',
                  animation: 'glow 0.5s ease-in-out 4'
                } : undefined}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">{restaurant.name}</h2>
                    <p className="text-gray-600 text-sm mb-3">{restaurant.description}</p>
                    <div className="flex flex-col gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <i className="ri-map-pin-line text-base"></i>
                        <span>{restaurant.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-phone-line text-base"></i>
                        <a href={`tel:${restaurant.phone}`} className="hover:text-pink-600 transition-colors cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          {restaurant.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-lg"></i>
                      <span className="text-lg font-bold">{restaurant.rating}</span>
                    </div>
                    <div className="text-base font-bold text-pink-600">{restaurant.priceRange}</div>
                  </div>
                </div>

                {/* Restaurant Image Popup */}
                {selectedRestaurant === restaurant.id && (
                  <div className="mt-4 pt-4 border-t-4 border-black">
                    <div className="w-full h-48 bg-gray-100 border-4 border-black overflow-hidden">
                      <img 
                        src={restaurant.id === 1 
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/dc59fdaf83118b92ece1abefd755df68.jpeg"
                          : restaurant.id === 2
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/47c4849d5ff7a7f2c798a8e7ffd85e17.jpeg"
                          : restaurant.id === 3
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/91fe403987623843462e648004d53aa6.jpeg"
                          : restaurant.id === 4
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/b3f1238501a999bcf8d6769a07a4bfd0.jpeg"
                          : restaurant.id === 5
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/04afee51825b276898c231af1bbe9bd0.jpeg"
                          : restaurant.id === 6
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/6902b5493160e2ef5299a50d15193846.jpeg"
                          : "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/7e9f1a2b3c4d5e6f7g8h9i0j1k2l3m4n.jpeg"
                        }
                        alt={restaurant.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex justify-center gap-4">
            <Link 
              to="/"
              className="inline-block bg-pink-400 hover:bg-pink-500 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
            >
              ← BACK TO CATEGORIES
            </Link>
            {hasMore && (
              <button
                onClick={handleLoadMore}
                className="inline-block bg-pink-400 hover:bg-pink-500 text-white font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
              >
                MORE →
              </button>
            )}
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 border-t-4 border-black">
        <div className="container mx-auto text-center">
          <p className="text-sm mb-2">© 2025 Antalya Eats. Your guide to the best restaurants in Antalya.</p>
          <a href="https://readdy.ai/?ref=logo" className="text-xs text-gray-400 hover:text-white whitespace-nowrap">Website Builder</a>
        </div>
      </footer>
    </div>
  );
}
